import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useLicense } from "../../contexts/LicenseContext";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Clock, Crown, X, Sparkles, CreditCard } from "lucide-react";

/**
 * Daily Trial Reminder Modal
 *
 * يظهر مرة واحدة فقط في اليوم للمستخدمين التجريبيين (من اليوم 2 فصاعداً)
 * — بعد اليوم الأول حتى لا نُزعج المستخدم في أول تجربة.
 *
 * - اليوم 2-3: تذكير ودّي (أخضر/أصفر)
 * - اليوم 4: تذكير جادّ (برتقالي)
 * - اليوم 5 / اليوم الأخير: عاجل (أحمر + لا يمكن إخفاؤه سريعاً)
 *
 * التخزين: localStorage key "trial_reminder_last_shown" = YYYY-MM-DD
 */
const STORAGE_KEY = "trial_reminder_last_shown";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export default function TrialReminderModal() {
  const { user } = useAuth();
  const { promptUpgrade, isOwner } = useLicense();
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    if (isOwner || !user) return;
    if (user.license_type !== "trial" || !user.trial_end_date) return;

    const end = new Date(user.trial_end_date);
    const diffDays = Math.ceil((end - new Date()) / 86400000);
    setDaysLeft(diffDays);

    // شروط عدم الظهور
    if (diffDays < 0) return; // انتهت
    // يظهر فقط في آخر يومين من التجربة (تذكير ضروري). قبل ذلك TrialBanner في الأعلى يكفي.
    if (diffDays > 2) return;
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown === todayKey()) return; // عُرض اليوم

    // تأخير بسيط حتى لا يظهر فور فتح الصفحة
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user, isOwner]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, todayKey());
    setOpen(false);
  };

  const upgradeNow = () => {
    localStorage.setItem(STORAGE_KEY, todayKey());
    setOpen(false);
    promptUpgrade("trial_reminder");
  };

  if (!open || daysLeft === null) return null;

  // تدرّج اللون حسب الأيام المتبقية
  const urgency =
    daysLeft <= 1 ? "critical" :
    daysLeft <= 2 ? "urgent" :
    daysLeft <= 3 ? "warning" : "info";

  const urgencyStyles = {
    critical: { bg: "from-red-600 to-rose-600", icon: "from-red-400 to-rose-500", text: "text-red-400", pulse: true },
    urgent:   { bg: "from-orange-600 to-red-500", icon: "from-orange-400 to-red-500", text: "text-orange-400", pulse: true },
    warning:  { bg: "from-amber-500 to-orange-500", icon: "from-amber-400 to-orange-500", text: "text-amber-400", pulse: false },
    info:     { bg: "from-emerald-500 to-teal-500", icon: "from-emerald-400 to-teal-500", text: "text-emerald-400", pulse: false },
  }[urgency];

  const headline =
    daysLeft === 0 ? "اليوم آخر يوم من تجربتك!" :
    daysLeft === 1 ? "يوم واحد فقط يفصلك عن انتهاء التجربة" :
    `تبقى لديك ${daysLeft} ${daysLeft === 2 ? "يومان" : "أيام"} من التجربة المجانية`;

  const subline =
    daysLeft <= 1
      ? "اشترك الآن قبل فوات الأوان وتحتفظ بكل بياناتك ومزاياك"
      : "جرّب كل المزايا! وحين تقرّر، اشترك بنقرة واحدة";

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
      data-testid="trial-reminder-modal"
    >
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-amber-500/40 shadow-2xl overflow-hidden">
        {/* شريط علوي مُلوّن */}
        <div className={`h-2 bg-gradient-to-r ${urgencyStyles.bg} ${urgencyStyles.pulse ? "animate-pulse" : ""}`} />

        <CardContent className="p-6 relative">
          {daysLeft > 1 && (
            <button
              onClick={dismiss}
              className="absolute left-3 top-3 text-slate-500 hover:text-white transition"
              aria-label="إغلاق"
              data-testid="trial-reminder-dismiss-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {/* الأيقونة + العدّاد */}
          <div className="text-center mb-5">
            <div className={`w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br ${urgencyStyles.icon} flex items-center justify-center shadow-lg ${urgencyStyles.pulse ? "animate-pulse" : ""}`}>
              {urgency === "critical" || urgency === "urgent"
                ? <Clock className="w-10 h-10 text-white" />
                : <Sparkles className="w-10 h-10 text-white" />}
            </div>
            <div className={`inline-block px-4 py-1.5 rounded-full bg-slate-800 border ${urgencyStyles.text.replace("text-", "border-")}/40`}>
              <span className={`text-5xl font-extrabold ${urgencyStyles.text}`} dir="ltr">{Math.max(daysLeft, 0)}</span>
              <span className="text-slate-400 text-sm mr-2">
                {daysLeft === 0 ? "أيام" : daysLeft === 1 ? "يوم متبقّي" : "أيام متبقّية"}
              </span>
            </div>
          </div>

          {/* النص */}
          <h2 className="text-xl font-bold text-white text-center mb-2" data-testid="trial-reminder-headline">
            {headline}
          </h2>
          <p className="text-slate-400 text-center text-sm mb-5">
            {subline}
          </p>

          {/* السعر مميّز */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-4 text-center mb-5">
            <p className="text-slate-400 text-xs mb-1">السعر الموحد لتفعيل التطبيق الكامل</p>
            <p className="text-3xl font-extrabold text-emerald-400">
              690,000 <span className="text-base text-slate-400 font-normal">د.ع</span>
            </p>
            <p className="text-slate-500 text-xs mt-1">دفعة واحدة — ترخيص دائم</p>
          </div>

          {/* أزرار الإجراء */}
          <div className="space-y-2">
            <Button
              onClick={upgradeNow}
              className={`w-full bg-gradient-to-r ${urgencyStyles.bg} hover:opacity-90 text-white py-6 text-base font-bold shadow-lg`}
              data-testid="trial-reminder-upgrade-btn"
            >
              <Crown className="w-5 h-5 ml-2" />
              اشترك الآن واحتفظ بكل بياناتك
            </Button>
            {daysLeft > 1 && (
              <Button
                onClick={dismiss}
                variant="ghost"
                className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="trial-reminder-later-btn"
              >
                ذكّرني غداً
              </Button>
            )}
          </div>

          {/* طرق الدفع السريعة */}
          <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-center gap-2 text-xs text-slate-500 flex-wrap">
            <CreditCard className="w-3.5 h-3.5" />
            <span>سوبر كي</span>
            <span>•</span>
            <span>زين كاش</span>
            <span>•</span>
            <span>آسيا باي</span>
            <span>•</span>
            <span>ناس واليت</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
